import React from 'react';
import { usePermissionGuards } from '@/hooks/usePermissionGuards';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface ActionGuardProps {
  /**
   * Direct boolean override. If provided, overrides internal permission checks.
   */
  allowed?: boolean;

  /**
   * Module name for permission lookup (e.g. 'quotations', 'orders', 'production', 'reconciliations')
   */
  module?: string;

  /**
   * Action name (e.g. 'approve', 'create', 'update', 'delete')
   */
  action?: string;

  /**
   * @deprecated Không còn dùng (04/11/2026): quyền đọc từ bảng phân quyền qua module + action
   */
  roles?: string[];

  /**
   * Tooltip message displayed when action is disabled
   */
  fallbackMessage?: string;

  /**
   * If true, hide component entirely instead of rendering disabled
   */
  hideIfForbidden?: boolean;

  children: React.ReactElement;
}

/**
 * ActionGuard — UI Security Wrapper component
 * Guards sensitive interactive elements (Buttons, Actions) with ISO 9001:2015 permission validation.
 */
export function ActionGuard({
  allowed,
  module,
  action,
  fallbackMessage = 'Chỉ Giám Đốc hoặc Trưởng bộ phận mới có quyền thực hiện thao tác này',
  hideIfForbidden = false,
  children,
}: ActionGuardProps) {
  const { checkActionAllowed } = usePermissionGuards();

  let isAllowed = true;

  if (typeof allowed === 'boolean') {
    isAllowed = allowed;
  } else if (module && action) {
    isAllowed = checkActionAllowed(module, action);
  }

  if (isAllowed) {
    return children;
  }

  if (hideIfForbidden) {
    return null;
  }

  // Clone child with disabled prop
  const disabledChild = React.cloneElement(children as any, {
    disabled: true,
    className: `${(children as any).props.className || ''} opacity-60 cursor-not-allowed pointer-events-none`,
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-block cursor-not-allowed">{disabledChild}</span>
      </TooltipTrigger>
      <TooltipContent className="bg-slate-900 text-white text-xs max-w-xs shadow-lg p-2 rounded-md">
        <p className="flex items-center gap-1.5 font-medium text-amber-300">
          🔒 Giới hạn quyền hạn
        </p>
        <p className="text-[11px] text-slate-300 mt-0.5">{fallbackMessage}</p>
      </TooltipContent>
    </Tooltip>
  );
}
